// start-turn-gm.ts — per-viewer start-of-turn notification.
//
// On a `start_turn` roll frame, the start-of-turn CHIME is a NOTIFICATION that
// only fires when the creature whose turn just started is the viewer's own
// active caster — i.e. the sheet they have pulled up
// (window.FateTide.activeViewerCasterId). Otherwise the default server-resolved
// sound (end_turn) plays. This stops the chime from firing on every creature's
// turn (e.g. the GM pushing time on my_campaign.php).
//
// GM carve-out (owner spec 2026-07-13): when the up-next actor is a
// GM-controlled NPC or lair (item_type 'creature'/'lair' with NO
// controller_encounter_creature_id — mirrors PHP is_creature_not_a_summon for
// ordinary creatures), the GM's client TAKES CONTROL of it —
// the same gesture as double-clicking the creature on the GM viewer
// (template.js ftSetActiveCaster + cache-first card render into
// div_refresh_gm_viewer) — and the chime fires, so the GM is audibly reminded
// they now drive this turn. On pages WITHOUT a GM viewer (item.php map view),
// we fall back to the older read-only auto-open in the side-info drawer, silent.
//
// Returns true ONLY when it owns the frame's audio (so handleRollInsert
// skips the frame's own playRollSound). Returns false otherwise → the frame's
// routed sound plays.

const SIDE_INFO_CONTAINER = 'div_refresh_side_info';
let _chimeEl: HTMLAudioElement | null = null;

// Generation counter for the GM auto-take-control path: the 0-AP auto-end loop can emit
// several start_turn frames in one server request, and ftSetActiveCaster's subscribe is
// async — without this, an earlier frame resolving late could render a stale card (and
// each cascade frame would chime). Only the latest generation renders + chimes.
let _gmAutoLoadGen = 0;

function playStartTurnChime(): void {
    const url = window.FT_START_TURN_CHIME_URL || '';
    if (!url) return;
    try {
        if (!_chimeEl) _chimeEl = new Audio(url);
        _chimeEl.currentTime = 0;
        window.ftRegisterSiteAudioTransient?.(_chimeEl);
        void _chimeEl.play();
    } catch {
        /* autoplay policy / no user gesture yet — silently ignore */
    }
}

// encounter_creatures fields land as bare scalars in the cache, but tolerate a
// [0]-wrapped shape just in case.
function readScalar(v: unknown): string {
    if (Array.isArray(v)) return v.length ? String(v[0] ?? '') : '';
    return v == null ? '' : String(v);
}

function sameId(a: string, b: string): boolean {
    return !!a && !!b && a.toUpperCase() === b.toUpperCase();
}

type TurnBlob = {
    encounter_creatures?: { controller_encounter_creature_id?: unknown; item_type?: unknown };
    items?: { item_type?: unknown };
    player_characters?: { item_id?: unknown };
};

function cachedTurnBlob(ecId: string): TurnBlob | undefined {
    try { window.ftSeedCasterFromEncounterCache?.(ecId); } catch { /* best effort */ }
    return window.FateTide?.casterCache?.get(ecId) as TurnBlob | undefined;
}

// Is this creature the GM's to drive? An uncontrolled NPC/lair, or a summon whose controller is itself
// GM-driven (owner 2026-09-14 — same rule as my_campaign.php ft3dTurnIsGmDriven). Player characters and
// player-controlled summons are not. Only POSITIVELY cached rows count: a miss anywhere in the controller
// chain is unresolved → false, so the GM keeps their caster.
function isGmDriven(blob: TurnBlob | undefined, depth = 0): boolean {
    if (!blob || depth > 4) return false;
    if (readScalar(blob.player_characters?.item_id).trim()) return false;
    const itemType = (readScalar(blob.items?.item_type).trim()
        || readScalar(blob.encounter_creatures?.item_type).trim()).toLowerCase();
    if (itemType !== 'creature' && itemType !== 'lair') return false;
    const controller = readScalar(blob.encounter_creatures?.controller_encounter_creature_id).trim();
    if (!controller) return true;
    return isGmDriven(cachedTurnBlob(controller), depth + 1);
}

export function handleStartTurnNotification(roll: Record<string, unknown> | undefined): boolean {
    if (!roll) return false;
    // start_legendary_turn (Legendary Turns) is a start_turn in every audio/auto-load
    // respect — only the sidelog line differs.
    const rollType = String(roll.roll_type_1 ?? '');
    if (rollType !== 'start_turn' && rollType !== 'start_legendary_turn') return false;

    // roll_by is the up-next creature's encounter_creature_id (insert_log_message).
    const startedEcId = String(roll.roll_by ?? '').trim();
    if (!startedEcId) return false;

    // GM convenience: the up-next GM-driven NPC or lair (uncontrolled, or summoned by a GM
    // creature — isGmDriven) becomes the GM's problem — on
    // GM pages (my_campaign.php / modify_encounter, detected by the GM viewer div)
    // auto-TAKE-CONTROL of it and load its card into the GM viewer, exactly like
    // the double-click gesture, then chime. Elsewhere keep the read-only side-info
    // auto-open, silent (viewing an NPC you don't control must not make a sound).
    // Player-character turns are deliberately excluded: the GM keeps the current
    // caster/sheet. FT3D turn-camera behavior remains independent of this control path.
    const startedItemType = String(roll.roll_by_item_type ?? '').toLowerCase();
    if (
        window.FateTide?.user?.isGM
        && (startedItemType === 'creature' || startedItemType === 'lair')
    ) {
        const blob = cachedTurnBlob(startedEcId);
        const controller = readScalar(blob?.encounter_creatures?.controller_encounter_creature_id);
        const gmViewer = document.getElementById('div_refresh_gm_viewer');
        // Take control only off a POSITIVELY cached, GM-driven row (isGmDriven): a cache miss also
        // reads as "no controller", which is fine for the read-only side-drawer open but must not
        // silently change who the GM is acting as (could be an unverified or player-owned summon).
        if (gmViewer && isGmDriven(blob) && typeof window.ftSetActiveCaster === 'function') {
            // If the GM already controls this creature, skip the takeover chime — the
            // fall-through active-caster check below chimes instead. Never both.
            const alreadyActive = sameId(
                String(window.FateTide?.activeViewerCasterId ?? '').trim(),
                startedEcId,
            );
            const gen = ++_gmAutoLoadGen;
            void (async () => {
                try {
                    await window.ftSetActiveCaster?.(startedEcId);
                    if (gen !== _gmAutoLoadGen) return; // superseded by a newer turn frame
                    const renderer = window.ftRenderCasterCardFromCache || window.ftRenderCreatureCardFromCache;
                    renderer?.(startedEcId, 'div_refresh_gm_viewer', {
                        cardColClasses: 'col-lg-6 col-md-12',
                        viewTarget: startedEcId,
                    });
                    // Chime only after control actually landed (a failed subscribe stays
                    // silent — the GM can still double-click as before).
                    if (!alreadyActive) playStartTurnChime();
                } catch { /* best effort — worst case the GM double-clicks as before */ }
            })();
        } else if (!controller && !window.__ftSideInfoMinigameActive) {
            // Skip the read-only side-info auto-open while a canvas minigame owns the drawer
            // (lockpick — claimSideInfoForMinigame): painting a creature card over a live
            // lockpick canvas makes the minigame cancel the attempt server-side.
            try {
                window.ftRenderCreatureCardFromCache?.(startedEcId, SIDE_INFO_CONTAINER, { sideInfo: true });
            } catch { /* best effort */ }
        }
    }

    // The chime fires ONLY when the started creature is the viewer's OWN active
    // caster (the creature they actively control / have pulled up). For any other
    // creature — including NPCs the GM merely auto-opened — the start_turn frame
    // stays SILENT: the separate end_turn frame plays the boundary sound, so we
    // never stack chime+end_turn (or end_turn+end_turn).
    const activeId = String(window.FateTide?.activeViewerCasterId ?? '').trim();
    if (sameId(activeId, startedEcId)) {
        playStartTurnChime();
    }

    // start_turn audio is fully owned here (chimed or intentionally silent) →
    // always skip the frame's own playRollSound for the start_turn frame.
    return true;
}
